import { animate, state, style, transition, trigger } from '@angular/animations';
import { DatePipe, formatDate } from '@angular/common';
import { HttpParams } from '@angular/common/http';
import { AfterViewInit, Component, Inject, OnInit, ViewChild } from '@angular/core';
import { UntypedFormControl } from '@angular/forms';
import { MatSort } from '@angular/material/sort';
import { MatTableDataSource } from '@angular/material/table';
import { ChartType } from 'angular-google-charts';
import { Subscription } from 'rxjs';
import { API_BASE_URL_SERVICE } from '../../../../../projects/kiene-core/src/lib/kiene-core.config';
import { MessageService } from '../../../../../projects/kiene-core/src/lib/services/message-service/message.service';
import { KieneTableService } from '../../../components/kiene-table/kiene-table.service';
import { CsvColumn } from '../../../csv-export/csv-export/csv-column';
import { ReportUtils } from '../../report-utils';
import { ZeitauswahlChange } from '../../zeitauswahl/zeitauswahl-change';
import { WarengruppenEingang } from './warengruppen-eingang';

@Component({
    selector: 'kiene-warengruppen-eingang-report',
    templateUrl: './warengruppen-eingang-report.component.html',
    styleUrls: ['./warengruppen-eingang-report.component.scss'],
    animations: [
        trigger('detailExpanded', [
            state('collapsed', style({ height: '0px', minHeight: '0' })),
            state('expanded', style({ height: '*' })),
            transition('expanded <=> collapsed', animate('225ms cubic-bezier(0.4, 0.0, 0.2, 1)')),
        ]),
    ],
})
export class WarengruppenEingangReportComponent implements OnInit, AfterViewInit {
    displayedColumns = ['warengruppe', 'warenwert', 'wareneingang'];
    dataSource = new MatTableDataSource<WarengruppenEingang>();
    dataSourceVorjahr = new MatTableDataSource<WarengruppenEingang>();
    @ViewChild(MatSort) sort: MatSort;
    expandedElement: WarengruppenEingang | null;

    tierarztCtrl = new UntypedFormControl();

    reportParams = new HttpParams();
    zeiteintrag: ZeitauswahlChange;
    vorjahreszeitraum: ZeitauswahlChange;

    chartType = ChartType.PieChart;
    chartColumns = ['Warengruppe', 'Warenwert'];
    chartData = [['', 0]];

    csvColumns: CsvColumn[] = [];
    csvColumnsDetails: CsvColumn[] = [];
    csvFilename = 'wareneingang.csv';

    gesamtWarenwert = 0;
    gesamtWarenwertVorjahr = 0;

    isLoading: boolean[] = [];
    aktuellSubscription: Subscription;
    vorjahrSubscription: Subscription;

    constructor(
        private api: KieneTableService,
        @Inject(API_BASE_URL_SERVICE) private apiBaseUrl: string,
        private datePipe: DatePipe,
        private messageService: MessageService,
    ) {}

    ngOnInit(): void {
        this.reportParams = this.reportParams.set('von', this.datePipe.transform(new Date(), 'yyyy-MM-dd'));
        this.reportParams = this.reportParams.set('bis', this.datePipe.transform(new Date(), 'yyyy-MM-dd'));

        this.csvColumns.push(new CsvColumn('warengruppe', 'Warengruppe'));
        this.csvColumns.push(new CsvColumn('warenwert', 'Wareneinsatz'));
        // this.csvColumns.push(new CsvColumn('artikel', 'Anz. untersch. Artikel'));
        this.csvColumns.push(new CsvColumn('wareneingang', 'Wareneingang'));

        this.csvColumnsDetails = this.csvColumns.map(column => ({ ...column }));
        this.csvColumnsDetails.splice(0, 1, new CsvColumn('zeitraum', 'Zeitraum'));
    }

    ngAfterViewInit() {
        this.dataSource.sort = this.sort;
    }

    loadData() {
        this.loadDataAktuell();
        this.loadDataVorjahr();
    }

    loadDataAktuell() {
        let params = new HttpParams();
        params = params.set('von', formatDate(this.zeiteintrag.von, 'yyyy-MM-dd', 'de'));
        params = params.set('bis', formatDate(this.zeiteintrag.bis, 'yyyy-MM-dd', 'de'));

        this.aktuellSubscription?.unsubscribe();
        this.aktuellSubscription = this.api
            .get(this.apiBaseUrl + 'report/read_waren_eingang_gruppe.php', params)
            .subscribe(
                response => {
                    this.dataSource.data = response.records;
                    this.resetChartData();
                    this.berechneGesamtwerte();
                },
                error => {
                    this.messageService.errorMessage('Fehler!', error);
                },
            );
    }

    loadDataVorjahr() {
        let params = new HttpParams();
        params = params.set('von', formatDate(this.vorjahreszeitraum.von, 'yyyy-MM-dd', 'de'));
        params = params.set('bis', formatDate(this.vorjahreszeitraum.bis, 'yyyy-MM-dd', 'de'));

        this.vorjahrSubscription?.unsubscribe();
        this.vorjahrSubscription = this.api
            .get(this.apiBaseUrl + 'report/read_waren_eingang_gruppe.php', params)
            .subscribe(
                response => {
                    this.dataSourceVorjahr.data = response.records;
                    this.berechneGesamtwerteVorjahr();
                },
                error => {
                    this.messageService.errorMessage('Fehler!', error);
                },
            );
    }

    zeitintervalChanged(event: ZeitauswahlChange) {
        this.zeiteintrag = event;
        this.vorjahreszeitraum = ReportUtils.getVorjahreszeitraum(this.zeiteintrag);
        this.loadData();
    }

    berechneGesamtwerte() {
        this.gesamtWarenwert = 0;
        const data = this.dataSource.data;
        for (const r of data) {
            this.chartData.push([r.warengruppe, r.warenwert ? r.warenwert : 0]);
            this.gesamtWarenwert += r.warenwert ? r.warenwert : 0;
        }
    }

    berechneGesamtwerteVorjahr() {
        this.gesamtWarenwertVorjahr = 0;
        const data = this.dataSourceVorjahr.data;
        for (const r of data) {
            this.gesamtWarenwertVorjahr += r.warenwert ? r.warenwert : 0;
        }
    }

    expandVetGroupRow(element: WarengruppenEingang) {
        this.expandedElement = this.expandedElement === element ? null : element;
        if (this.expandedElement) {
            this.isLoading[element.warengruppe_id] = true;

            let params = new HttpParams();
            params = params.set('von', formatDate(this.zeiteintrag.von, 'yyyy-MM-dd', 'de'));
            params = params.set('bis', formatDate(this.zeiteintrag.bis, 'yyyy-MM-dd', 'de'));
            params = params.set('aggregieren', this.zeiteintrag.aggregieren);
            params = params.set('warengruppe_id', element.warengruppe_id.toString());
            this.api.get(this.apiBaseUrl + 'report/read_waren_eingang_gruppe.php', params).subscribe(
                response => {
                    element.details = response.records;
                    this.isLoading[element.warengruppe_id] = false;
                },
                error => {
                    this.messageService.errorMessage('Fehler!', error);
                    this.isLoading[element.warengruppe_id] = false;
                },
            );
        }
    }

    resetChartData() {
        this.chartData = [['', 0]];
    }
}
